import {useContext, useEffect, useState} from 'react';
import {useLazyQuery} from '@apollo/client';
import {GET_VACATION_RULES_BY_SENIORITY} from 'graphql/absences/Querys';
import {authContext} from 'contextApi/context/auth';

export interface VacationRule {
  fromYear: number;
  toYear: number;
  vacationDays: number;
  label: string;
}

export interface CountryInfo {
  code: string;
  name: string;
}

export interface VacationRulesResponse {
  status: 'success' | 'error';
  country?: CountryInfo;
  rules: VacationRule[];
  timestamp: string;
}

export const useVacationRules = () => {
  const {userDetails} = useContext(authContext);
  const country = userDetails?.data?.country;
  const [data, setData] = useState<VacationRulesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fetchRules] = useLazyQuery(GET_VACATION_RULES_BY_SENIORITY);

  const fetchData = async () => {
    if (!country) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const {data: gqlData} = await fetchRules({
        variables: {country},
      });
      setData(gqlData?.vacationRulesBySeniority || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Depend only on country change to avoid request loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  return {
    data,
    isLoading,
    error,
    retry: fetchData,
  };
};
